/**
 * Marketing App — lightweight alternative to app.tsx for public routes.
 *
 * Provides a stub AuthContext (user=null, loading=false) so marketing pages
 * that call useAuth() work correctly without importing firebase.
 *
 * Excluded providers (not needed on public pages):
 * - AuthProvider (real one) — triggers firebase lazy-load
 * - SubscriptionProvider — imports firebase/firestore statically
 * - QueryClientProvider — not used by marketing pages
 * - GuidedTourProvider — dashboard-only
 * - CookieConsentProvider — deferred, can be added later
 *
 * Navigation to /login, /dashboard, /onboarding triggers a full-page load
 * so the browser switches to the full app entry (200.html / index.html).
 */
import { BrowserRouter as Router, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import { Suspense, useEffect, useMemo, useState } from "react";
import { lazyWithRetry } from "./utils/lazyWithRetry";
import { CssBaseline } from "@mui/material";
import { AuthContext } from "./contexts/AuthContext";

// i18n initialization (side-effect import — must be before any component renders)
import "./i18n";
import { useTranslation } from "react-i18next";

import AppThemeMarketing from "./theme/AppThemeMarketing";
import ErrorBoundary from "./components/ErrorBoundary";

// Lazy-loaded marketing route components
const LandingProposalC = lazyWithRetry(() => import("./pages/LandingProposalC"));
const BlogPage = lazyWithRetry(() => import("./pages/BlogPage"));
const BlogPostPage = lazyWithRetry(() => import("./pages/BlogPostPage"));
const ScorePage = lazyWithRetry(() => import("./pages/ScorePage"));

// ExperimentsProvider is lazy so firebase/app + firebase/remote-config stay
// out of the initial marketing chunk (landing/blog/pricing don't run A/B
// tests). Routes that need experiments wrap their element with it.
const ExperimentsProvider = lazyWithRetry(() =>
  import("./contexts/ExperimentsProvider").then((m) => ({ default: m.ExperimentsProvider }))
);
const OutilsHubPage = lazyWithRetry(() => import("./pages/OutilsHubPage"));
const AnalyseurATSPage = lazyWithRetry(() => import("./pages/AnalyseurATSPage"));
const ModelesCVPage = lazyWithRetry(() => import("./pages/ModelesCVPage"));
const GuideCVSuissePage = lazyWithRetry(() => import("./pages/GuideCVSuissePage"));
const PricingPage = lazyWithRetry(() => import("./marketingComponents/PricingPage"));
const PrivacyPage = lazyWithRetry(() => import("./pages/PrivacyPage"));
const TermsPage = lazyWithRetry(() => import("./pages/TermsPage"));
const LegalPage = lazyWithRetry(() => import("./pages/LegalPage"));
const UnsubscribePage = lazyWithRetry(() => import("./pages/UnsubscribePage"));
const SubmitTestimonial = lazyWithRetry(() => import("./pages/SubmitTestimonial"));
const NotFoundPage = lazyWithRetry(() => import("./pages/NotFoundPage"));

/**
 * Stub AuthContext value — always unauthenticated.
 * Marketing pages see user=null and render the public/guest UI.
 */
const stubAuthValue = {
  user: null,
  loading: false,
  loginAsTestUser: () => {},
  logout: async () => {},
  isTestMode: false,
};

function MarketingApp() {
  const authValue = useMemo(() => stubAuthValue, []);

  return (
    <ErrorBoundary level="page">
      <AuthContext.Provider value={authValue}>
        <AppThemeMarketing>
          <CssBaseline enableColorScheme />
          <Router>
            <MarketingLanguageSync />
            <AppRedirectGuard />
            <ErrorBoundary level="page">
              <Suspense fallback={<RouteLoadingFallback />}>
                <Routes>
                  <Route
                    path="/"
                    element={
                      <ExperimentsProvider>
                        <LandingProposalC />
                      </ExperimentsProvider>
                    }
                  />
                  <Route
                    path="/landing"
                    element={
                      <ExperimentsProvider>
                        <LandingProposalC />
                      </ExperimentsProvider>
                    }
                  />
                  <Route
                    path="/landing-c"
                    element={
                      <ExperimentsProvider>
                        <LandingProposalC />
                      </ExperimentsProvider>
                    }
                  />
                  <Route path="/blog" element={<BlogPage />} />
                  <Route path="/blog/:slug" element={<BlogPostPage />} />
                  <Route path="/de/blog" element={<BlogPage lang="de" />} />
                  <Route path="/de/blog/:slug" element={<BlogPostPage />} />
                  <Route
                    path="/score"
                    element={
                      <ExperimentsProvider>
                        <ScorePage />
                      </ExperimentsProvider>
                    }
                  />
                  <Route path="/outils" element={<OutilsHubPage />} />
                  <Route path="/outils/analyseur-ats" element={<AnalyseurATSPage />} />
                  <Route path="/outils/modeles-cv" element={<ModelesCVPage />} />
                  <Route path="/guide/cv-suisse" element={<GuideCVSuissePage />} />
                  <Route path="/testimonial/submit" element={<SubmitTestimonial />} />
                  <Route
                    path="/pricing"
                    element={
                      <ExperimentsProvider>
                        <PricingPage />
                      </ExperimentsProvider>
                    }
                  />
                  <Route path="/privacy" element={<PrivacyPage />} />
                  <Route path="/terms" element={<TermsPage />} />
                  <Route path="/legal" element={<LegalPage />} />
                  <Route path="/unsubscribe" element={<UnsubscribePage />} />
                  {/* Any non-marketing route → full-page load to app entry */}
                  <Route path="*" element={<NotFoundPage />} />
                </Routes>
              </Suspense>
            </ErrorBoundary>
          </Router>
        </AppThemeMarketing>
      </AuthContext.Provider>
    </ErrorBoundary>
  );
}

/**
 * Redirect guard: if the user somehow lands on an app-only route
 * via the marketing entry, do a full page load to switch to the app entry.
 */
function AppRedirectGuard() {
  const location = useLocation();

  useEffect(() => {
    const appOnlyPrefixes = ['/login', '/signin', '/dashboard', '/onboarding'];
    const isAppRoute = appOnlyPrefixes.some(
      (prefix) => location.pathname === prefix || location.pathname.startsWith(prefix + '/')
    );
    if (isAppRoute) {
      // Full page load — browser will serve 200.html (full app entry)
      window.location.replace(location.pathname + location.search + location.hash);
    }
  }, [location]);

  return null;
}

/**
 * Lightweight language sync — only reads from localStorage/browser.
 * Does NOT sync with Firestore (no firebase import).
 */
function MarketingLanguageSync() {
  const { i18n } = useTranslation();

  useEffect(() => {
    document.documentElement.lang = i18n.language === 'de-CH' ? 'de' : i18n.language;
  }, [i18n.language]);

  return null;
}

/**
 * Lightweight route-level fallback — invisible for fast loads,
 * shows a minimal spinner only after 300ms delay.
 */
function RouteLoadingFallback() {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 300);
    return () => clearTimeout(t);
  }, []);
  if (!visible) return <div style={{ minHeight: '100vh' }} />;
  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      opacity: 0,
      animation: 'routeFadeIn 0.3s ease 0s forwards',
    }}>
      <style>{`@keyframes routeFadeIn { to { opacity: 0.5; } }`}</style>
      <div style={{
        width: 32,
        height: 32,
        borderRadius: '50%',
        border: '3px solid #DCFCE7',
        borderTopColor: '#22C55E',
        animation: 'routeSpin 0.8s linear infinite',
      }} />
      <style>{`@keyframes routeSpin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

export default MarketingApp;
